import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { getDocument } from '@atlaskit/browser-apis';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fullPageMessages } from '@atlaskit/editor-common/messages';
import { EditorToolbarProvider, EditorToolbarUIProvider } from '@atlaskit/editor-common/toolbar';
import { Popup, EDIT_AREA_ID } from '@atlaskit/editor-common/ui';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { calculateToolbarPositionTrackHead, calculateToolbarPositionOnCellSelection } from '@atlaskit/editor-common/utils';
import { AllSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { ToolbarSection, ToolbarButtonGroup, ToolbarDropdownItemSection, useToolbarUI } from '@atlaskit/editor-toolbar';
import { ToolbarModelRenderer } from '@atlaskit/editor-toolbar-model';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { SELECTION_TOOLBAR_LABEL } from '../consts';
import { getFocusableElements, isShortcutToFocusToolbar } from '../utils/toolbar';
var isToolbarComponent = function isToolbarComponent(component) {
  return component.type === 'toolbar' && component.key === 'inline-text-toolbar';
};
var usePluginState = conditionalHooksFactory(function () {
  return expValEquals('platform_editor_toolbar_aifc_patch_3', 'isEnabled', true);
}, function (api) {
  return useSharedPluginStateWithSelector(api, ['connectivity', 'userPreferences', 'toolbar', 'selection', 'userIntent', 'editorViewMode'], function (state) {
    var _state$connectivitySt, _state$userPreference, _state$toolbarState, _state$toolbarState2, _state$selectionState, _state$userIntentStat, _state$editorViewMode;
    return {
      connectivityStateMode: (_state$connectivitySt = state.connectivityState) === null || _state$connectivitySt === void 0 ? void 0 : _state$connectivitySt.mode,
      editorToolbarDockingPreference: (_state$userPreference = state.userPreferencesState) === null || _state$userPreference === void 0 || (_state$userPreference = _state$userPreference.preferences) === null || _state$userPreference === void 0 ? void 0 : _state$userPreference.toolbarDockingPosition,
      shouldShowToolbar: (_state$toolbarState = state.toolbarState) === null || _state$toolbarState === void 0 ? void 0 : _state$toolbarState.shouldShowToolbar,
      selectedNode: (_state$toolbarState2 = state.toolbarState) === null || _state$toolbarState2 === void 0 ? void 0 : _state$toolbarState2.selectedNode,
      selection: (_state$selectionState = state.selectionState) === null || _state$selectionState === void 0 ? void 0 : _state$selectionState.selection,
      currentUserIntent: (_state$userIntentStat = state.userIntentState) === null || _state$userIntentStat === void 0 ? void 0 : _state$userIntentStat.currentUserIntent,
      editorViewMode: (_state$editorViewMode = state.editorViewModeState) === null || _state$editorViewMode === void 0 ? void 0 : _state$editorViewMode.mode
    };
  });
}, function (api) {
  var connectivityStateMode = useSharedPluginStateSelector(api, 'connectivity.mode');
  var editorToolbarDockingPreference = useSharedPluginStateSelector(api, 'userPreferences.preferences.toolbarDockingPosition');
  var currentUserIntent = useSharedPluginStateSelector(api, 'userIntent.currentUserIntent');
  var selection = useSharedPluginStateSelector(api, 'selection.selection');
  var _useSharedPluginState = useSharedPluginStateWithSelector(api, ['toolbar'], function (state) {
      var _state$toolbarState3, _state$toolbarState4;
      return {
        shouldShowToolbar: (_state$toolbarState3 = state.toolbarState) === null || _state$toolbarState3 === void 0 ? void 0 : _state$toolbarState3.shouldShowToolbar,
        selectedNode: (_state$toolbarState4 = state.toolbarState) === null || _state$toolbarState4 === void 0 ? void 0 : _state$toolbarState4.selectedNode
      };
    }),
    shouldShowToolbar = _useSharedPluginState.shouldShowToolbar,
    selectedNode = _useSharedPluginState.selectedNode;
  return {
    connectivityStateMode: connectivityStateMode,
    editorToolbarDockingPreference: editorToolbarDockingPreference,
    currentUserIntent: currentUserIntent,
    shouldShowToolbar: shouldShowToolbar,
    selection: selection,
    editorViewMode: undefined,
    selectedNode: selectedNode
  };
});
export var SelectionToolbar = function SelectionToolbar(_ref) {
  var _api$toolbar;
  var api = _ref.api,
    editorView = _ref.editorView,
    mountPoint = _ref.mountPoint,
    disableSelectionToolbarWhenPinned = _ref.disableSelectionToolbarWhenPinned;
  var _usePluginState = usePluginState(api),
    connectivityStateMode = _usePluginState.connectivityStateMode,
    editorToolbarDockingPreference = _usePluginState.editorToolbarDockingPreference,
    currentUserIntent = _usePluginState.currentUserIntent,
    shouldShowToolbar = _usePluginState.shouldShowToolbar,
    editorViewMode = _usePluginState.editorViewMode,
    selection = _usePluginState.selection;
  var intl = useIntl();
  var components = api === null || api === void 0 || (_api$toolbar = api.toolbar) === null || _api$toolbar === void 0 ? void 0 : _api$toolbar.actions.getComponents();
  var toolbar = components === null || components === void 0 ? void 0 : components.find(function (component) {
    return isToolbarComponent(component);
  });
  var keyboardNavigation = useMemo(function () {
    return getKeyboardNavigationConfig(editorView, intl, api);
  }, [editorView, intl, api]);
  var _useToolbarUI = useToolbarUI(),
    isDisabled = _useToolbarUI.isDisabled;
  var patch6Enabled = expValEquals('platform_editor_toolbar_aifc_patch_6', 'isEnabled', true);
  var isOffline = connectivityStateMode === 'offline';
  var isTextSelection = !editorView.state.selection.empty && editorView.state.selection instanceof TextSelection;
  var isAllSelection = !editorView.state.selection.empty && editorView.state.selection instanceof AllSelection;
  var isCellSelection = !editorView.state.selection.empty && '$anchorCell' in editorView.state.selection;
  var onPositionCalculated = useCallback(function (position) {
    var toolbarTitle = SELECTION_TOOLBAR_LABEL;

    // Show special position on cell selection only when editor controls experiment is enabled
    var isEditorControlsEnabled = expValEquals('platform_editor_controls', 'cohort', 'variant1');
    var isCellSelection = ('$anchorCell' in editorView.state.selection);
    if (isCellSelection && isEditorControlsEnabled) {
      return calculateToolbarPositionOnCellSelection(toolbarTitle)(editorView, position);
    }
    return calculateToolbarPositionTrackHead(toolbarTitle)(editorView, position);
  }, [editorView]);
  if (expValEquals('platform_editor_toolbar_aifc_template_editor', 'isEnabled', true) && editorToolbarDockingPreference === 'top' && disableSelectionToolbarWhenPinned || !components || !toolbar) {
    return null;
  }
  if (!(isTextSelection || isCellSelection || isAllSelection) || currentUserIntent === 'dragging' || !shouldShowToolbar || currentUserIntent === 'blockMenuOpen' && expValEqualsNoExposure('platform_editor_block_menu', 'isEnabled', true) || currentUserIntent && currentUserIntent !== 'default' || isSSR()) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Popup, {
    offset: [0, 10],
    target: getDomRefFromSelection(editorView),
    onPositionCalculated: onPositionCalculated,
    mountTo: mountPoint
  }, /*#__PURE__*/React.createElement(EditorToolbarProvider, {
    editorView: editorView,
    editorToolbarDockingPreference: editorToolbarDockingPreference,
    editorViewMode: expValEquals('platform_editor_toolbar_aifc_patch_4', 'isEnabled', true) ? editorViewMode !== null && editorViewMode !== void 0 ? editorViewMode : 'edit' : editorViewMode
  }, /*#__PURE__*/React.createElement(EditorToolbarUIProvider, {
    api: api,
    isDisabled: patch6Enabled ? isDisabled : isOffline
    // supress type checks for now
    ,
    fireAnalyticsEvent: expValEquals('platform_editor_toolbar_aifc_toolbar_analytic', 'isEnabled', true) ? function (payload) {
      var _api$analytics;
      api === null || api === void 0 || (_api$analytics = api.analytics) === null || _api$analytics === void 0 || _api$analytics.actions.fireAnalyticsEvent(payload);
    } : undefined,
    keyboardNavigation: expValEquals('platform_editor_toolbar_aifc_patch_5', 'isEnabled', true) ? keyboardNavigation : undefined
  }, /*#__PURE__*/React.createElement(ToolbarModelRenderer, {
    toolbar: toolbar,
    components: components,
    fallbacks: {
      section: ToolbarSection,
      menuSection: ToolbarDropdownItemSection,
      group: ToolbarButtonGroup
    }
  }))));
};
var getDomRefFromSelection = function getDomRefFromSelection(view
// dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
) {
  try {
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    return findDomRefAtPos(view.state.selection.from, view.domAtPos.bind(view));
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error) {
    // // eslint-disable-next-line no-console
    // console.warn(error);
    // if (dispatchAnalyticsEvent) {
    // 	const payload: AnalyticsEventPayload = {
    // 		action: ACTION.ERRORED,
    // 		actionSubject: ACTION_SUBJECT.CONTENT_COMPONENT,
    // 		eventType: EVENT_TYPE.OPERATIONAL,
    // 		attributes: {
    // 			component: CONTENT_COMPONENT.FLOATING_TOOLBAR,
    // 			selection: view.state.selection.toJSON(),
    // 			position: view.state.selection.from,
    // 			docSize: view.state.doc.nodeSize,
    // 			error: error.toString(),
    // 			// @ts-expect-error - Object literal may only specify known properties, 'errorStack' does not exist in type
    // 			// This error was introduced after upgrading to TypeScript 5
    // 			errorStack: error.stack || undefined,
    // 		},
    // 	};
    // 	dispatchAnalyticsEvent(payload);
    // }
  }
};
var getKeyboardNavigationConfig = function getKeyboardNavigationConfig(editorView, intl, api) {
  if (!(editorView.dom instanceof HTMLElement)) {
    return;
  }
  var toolbarSelector = "[data-testid='editor-floating-toolbar']";
  return {
    childComponentSelector: toolbarSelector,
    dom: editorView.dom,
    isShortcutToFocusToolbar: isShortcutToFocusToolbar,
    handleFocus: function handleFocus(event) {
      var _getDocument, _filteredFocusableEle, _filteredFocusableEle2, _filteredFocusableEle3;
      var toolbar = (_getDocument = getDocument()) === null || _getDocument === void 0 ? void 0 : _getDocument.querySelector(toolbarSelector);
      if (!(toolbar instanceof HTMLElement)) {
        return;
      }
      var filteredFocusableElements = getFocusableElements(toolbar);
      (_filteredFocusableEle = filteredFocusableElements[0]) === null || _filteredFocusableEle === void 0 || _filteredFocusableEle.focus();

      // the button element removes the focus ring so this class adds it back
      if (((_filteredFocusableEle2 = filteredFocusableElements[0]) === null || _filteredFocusableEle2 === void 0 ? void 0 : _filteredFocusableEle2.tagName) === 'BUTTON') {
        filteredFocusableElements[0].classList.add('first-floating-toolbar-button');
      }
      (_filteredFocusableEle3 = filteredFocusableElements[0]) === null || _filteredFocusableEle3 === void 0 || _filteredFocusableEle3.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
      event.preventDefault();
      event.stopPropagation();
    },
    handleEscape: function handleEscape(event) {
      var isDropdownOpen = !!document.querySelector('[data-toolbar-component="menu-section"]');
      if (isDropdownOpen) {
        return;
      }
      api === null || api === void 0 || api.core.actions.focus();
      event.preventDefault();
      event.stopPropagation();
    },
    ariaControls: EDIT_AREA_ID,
    ariaLabel: intl.formatMessage(fullPageMessages.toolbarLabel)
  };
};